import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createOfferApi,
  deleteOfferApi,
  getOfferApi,
  getOffersApi,
  updateOfferApi,
} from "../api/offersApi";
import {
  catalogQueryKey,
  type CatalogScope,
} from "../api/catalogScope";
import type { OfferId } from "@KassaroAB/shared/types/ids";
import type {
  RestCreateOffer,
  RestUpdateOffer,
} from "@KassaroAB/shared/types/offer";

const offersQueryKey = (scope: CatalogScope | null) =>
  catalogQueryKey(scope, "offers");

const invalidateOfferQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  scope: CatalogScope | null,
) => {
  queryClient.invalidateQueries({ queryKey: offersQueryKey(scope) });
};

export const useGetOffersQuery = (scope: CatalogScope | null) => {
  return useQuery({
    queryKey: offersQueryKey(scope),
    queryFn: () => getOffersApi(scope!),
    enabled: !!scope,
  });
};

export const useGetOfferQuery = (
  scope: CatalogScope | null,
  offerId: OfferId | null | undefined,
) => {
  return useQuery({
    queryKey: [...offersQueryKey(scope), offerId],
    queryFn: () => getOfferApi(scope!, offerId!),
    enabled: !!scope && !!offerId,
  });
};

export const useCreateOfferMutation = (scope: CatalogScope | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (offer: RestCreateOffer) => createOfferApi(scope!, offer),
    onSuccess: () => {
      invalidateOfferQueries(queryClient, scope);
    },
  });
};

export const useUpdateOfferMutation = (scope: CatalogScope | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      offerId,
      offer,
    }: {
      offerId: OfferId;
      offer: RestUpdateOffer;
    }) => updateOfferApi(scope!, offerId, offer),
    onSuccess: () => {
      invalidateOfferQueries(queryClient, scope);
    },
  });
};

export const useDeleteOfferMutation = (scope: CatalogScope | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (offerId: OfferId) => deleteOfferApi(scope!, offerId),
    onSuccess: () => {
      invalidateOfferQueries(queryClient, scope);
    },
  });
};
